import React, { useState } from 'react';
import useContainerWidth from './useContainerWidth';
import ChartTooltip from './ChartTooltip';
import { fmtCompact, fmtDay } from '../format';
import { cssZoom } from '../../../utils/zoom';

/**
 * One area+line per day. `series`: [{ key, label, values, color, format }].
 * Each series gets its own scale when `independent` is set (tokens vs. cost).
 */
const DailyLine = ({ days = [], series = [], height = 150 }) => {
  const [containerRef, measuredWidth] = useContainerWidth(600);
  const [hover, setHover] = useState(null);
  const w = Math.max(measuredWidth, 120);
  const padL = 40, padR = 8, padT = 12, padB = 22;
  const W = w - padL - padR;
  const H = height - padT - padB;
  const n = days.length;
  const x = (i) => padL + (n > 1 ? (i / (n - 1)) * W : W / 2);
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(W / 56))));
  const primary = series[0];
  const maxPrimary = Math.max(1, ...(primary ? primary.values : [0]));
  const empty = series.every((s) => s.values.every((v) => !v));

  const paths = series.map((s) => {
    const maxV = Math.max(1, ...s.values);
    const y = (v) => padT + H - ((v || 0) / maxV) * H;
    const pts = s.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    const line = pts.length ? `M${pts.join('L')}` : '';
    const area = pts.length ? `${line}L${x(n - 1).toFixed(1)},${(padT + H).toFixed(1)}L${x(0).toFixed(1)},${(padT + H).toFixed(1)}Z` : '';
    return { ...s, line, area, y };
  });

  const onMove = (e) => {
    if (!n) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / cssZoom();
    const i = Math.max(0, Math.min(n - 1, Math.round(((px - padL) / W) * (n - 1))));
    setHover(i);
  };

  return (
    <div ref={containerRef} className="qa-chart">
      <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={padL} x2={w - padR} y1={padT + H - f * H} y2={padT + H - f * H} className="qa-chart__grid" />
            <text x={padL - 6} y={padT + H - f * H + 3.5} textAnchor="end" className="qa-chart__tick">
              {primary ? (primary.format || fmtCompact)(maxPrimary * f) : ''}
            </text>
          </g>
        ))}
        {paths.map((p) => (
          <g key={p.key}>
            <path d={p.area} fill={p.color} opacity={0.12} />
            <path d={p.line} fill="none" stroke={p.color} strokeWidth={1.75} strokeLinejoin="round" />
          </g>
        ))}
        {hover != null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + H} className="qa-chart__cursor" />
            {paths.map((p) => (
              <circle key={p.key} cx={x(hover)} cy={p.y(p.values[hover])} r={3.5} fill={p.color} stroke="var(--surface-solid)" strokeWidth={1.5} />
            ))}
          </g>
        )}
        {days.map((day, i) => (i % labelEvery === 0 ? (
          <text key={day} x={x(i)} y={height - 6} textAnchor="middle" className="qa-chart__tick">{fmtDay(day)}</text>
        ) : null))}
        {empty && (
          <text x={padL + W / 2} y={padT + H / 2} textAnchor="middle" className="qa-chart__empty">No usage in this window</text>
        )}
      </svg>
      {hover != null && (
        <ChartTooltip
          x={Math.min(x(hover) + 10, w - 170)}
          y={8}
          title={fmtDay(days[hover])}
          rows={series.map((s) => ({ label: s.label, value: (s.format || fmtCompact)(s.values[hover] || 0), color: s.color }))}
        />
      )}
      <div className="qa-chart__legend">
        {series.map((s) => (
          <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>
        ))}
      </div>
    </div>
  );
};

export default DailyLine;
